/* eslint-disable @typescript-eslint/no-unused-vars */
import Web3 from 'web3';
import { Contract } from 'web3-eth-contract';
import { AbiItem } from 'web3-utils';
import { getWeb3, web3 } from '../web3';

interface EthereumProvider {
  request: (args: { method: string }) => Promise<string>;
  send: (method: string) => Promise<void>;
  enable: () => Promise<string[]>;
  selectedAddress: string | null;
  networkVersion: string;
}

declare global {
  interface Window {
    ethereum: EthereumProvider;
  }
}

interface Web3Services {
  getAccounts: () => Promise<string[]>;
  getNetworkId: () => Promise<string | number>;
  enableMetamask: () => Promise<boolean>;
  getContractInstance: <T extends AbiItem[]>(
    contractAbi: T,
    contractAddress: string
  ) => Promise<Contract<T>>;
  checkConnectionHealth: () => Promise<boolean>;
  getWeb3Instance: () => Promise<Web3 | null>;
  validateConnection: () => Promise<{ connected: boolean; accounts: string[]; error?: string }>;
  callContractMethod: <T>(
    contractMethod: { call: () => Promise<T> },
    options?: { timeout?: number; retries?: number; retryDelay?: number }
  ) => Promise<T>;
}

export const web3Services: Web3Services = {
  async getAccounts(): Promise<string[]> {
    try {
      let web3Instance = web3;
      if (!web3Instance || !web3Instance.eth) {
        web3Instance = await getWeb3();
        if (!web3Instance || !web3Instance.eth) {
          throw new Error('Web3 is not initialized');
        }
      }
      return await web3Instance.eth.getAccounts();
    } catch (error) {
      return [];
    }
  },

  async getNetworkId(): Promise<string | number> {
    try {
      let web3Instance = web3;
      if (!web3Instance || !web3Instance.eth) {
        web3Instance = await getWeb3();
        if (!web3Instance || !web3Instance.eth) {
          throw new Error('Web3 is not initialized');
        }
      }
      const networkId = await web3Instance.eth.net.getId();
      return networkId.toString();
    } catch (error) {
      return '';
    }
  },

  async enableMetamask(): Promise<boolean> {
    console.log('[enableMetamask] Called, typeof window:', typeof window);
    try {
      const hasEthereum =
        typeof window !== 'undefined' && !!(window as unknown as { ethereum?: unknown }).ethereum;
      console.log('[enableMetamask] window.ethereum present:', hasEthereum);
      if (!hasEthereum) {
        console.log('[enableMetamask] No ethereum provider, returning false');
        return false;
      }
      const ethereum = (
        window as unknown as {
          ethereum: { request: (args: { method: string; params?: unknown[] }) => Promise<unknown> };
        }
      ).ethereum;
      console.log('[enableMetamask] Calling eth_requestAccounts...');
      const accounts = await ethereum.request({ method: 'eth_requestAccounts', params: [] });
      const list = Array.isArray(accounts) ? accounts : accounts ? [accounts] : [];
      console.log('[enableMetamask] Accounts received, count:', list.length, 'accounts:', list);
      return list.length > 0;
    } catch (error: unknown) {
      const err = error as { code?: number; message?: string };
      console.log('[enableMetamask] Error:', err?.code, err?.message, err);
      if (err?.code === 4001) {
        console.warn('[enableMetamask] User rejected the request');
        return false;
      }
      if (err?.code === -32002) {
        console.warn('[enableMetamask] Request already pending');
        return false;
      }
      console.warn('[enableMetamask] Other error:', err);
      return false;
    }
  },

  async getContractInstance<T extends AbiItem[]>(
    contractAbi: T,
    contractAddress: string
  ): Promise<Contract<T>> {
    const maxRetries = 3;
    let retryCount = 0;
    let lastError: Error | null = null;

    while (retryCount < maxRetries) {
      try {
        let web3Instance = web3;
        if (!web3Instance || !web3Instance.eth) {
          web3Instance = await getWeb3();
          if (!web3Instance || !web3Instance.eth) {
            throw new Error('Web3 is not initialized');
          }
        }

        // Test Web3 connection before creating contract
        try {
          await web3Instance.eth.getBlockNumber();
        } catch (connectionError) {
          throw new Error(`Web3 connection test failed: ${connectionError.message}`);
        }

        // Validate contract address
        if (!contractAddress || !web3Instance.utils.isAddress(contractAddress)) {
          throw new Error(`Invalid contract address: ${contractAddress}`);
        }

        // Validate ABI
        if (!contractAbi || !Array.isArray(contractAbi) || contractAbi.length === 0) {
          throw new Error('Invalid contract ABI provided');
        }

        const contract = new web3Instance.eth.Contract(contractAbi, contractAddress);
        if (!contract) {
          throw new Error('Failed to create contract instance');
        }

        // Test contract by calling a simple method if available
        try {
          // Try to get the contract's code to verify it exists
          const code = await web3Instance.eth.getCode(contractAddress);
          if (code === '0x') {
            throw new Error(`No contract found at address: ${contractAddress}`);
          }
        } catch (codeError) {
          // Don't fail the entire operation for this
        }

        return contract;
      } catch (error) {
        retryCount++;
        lastError = error as Error;

        console.log(
          `Error creating contract instance (attempt ${retryCount}/${maxRetries}):`,
          error
        );

        if (retryCount < maxRetries) {
          // Wait before retrying with exponential backoff
          const delay = 1000 * Math.pow(2, retryCount - 1);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    // If all retries failed, throw the last error
    throw new Error(
      `Failed to create contract instance after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`
    );
  },

  async checkConnectionHealth(): Promise<boolean> {
    try {
      let web3Instance = web3;
      if (!web3Instance || !web3Instance.eth) {
        web3Instance = await getWeb3();
        if (!web3Instance || !web3Instance.eth) {
          return false;
        }
      }

      // Test multiple aspects of the connection
      const tests = [
        web3Instance.eth.getBlockNumber(),
        web3Instance.eth.net.getId(),
        web3Instance.eth.getGasPrice(),
      ];

      await Promise.all(tests);
      return true;
    } catch (error) {
      return false;
    }
  },

  async getWeb3Instance(): Promise<Web3 | null> {
    let web3Instance = web3;
    if (!web3Instance || !web3Instance.eth) {
      web3Instance = await getWeb3();
      if (!web3Instance || !web3Instance.eth) {
        throw new Error('Web3 is not initialized');
      }
    }

    // Test connection before returning
    await web3Instance.eth.getBlockNumber();
    return web3Instance;
  },

  async validateConnection(): Promise<{ connected: boolean; accounts: string[]; error?: string }> {
    try {
      let web3Instance = web3;
      if (!web3Instance || !web3Instance.eth) {
        web3Instance = await getWeb3();
        if (!web3Instance || !web3Instance.eth) {
          return { connected: false, accounts: [], error: 'Web3 not initialized' };
        }
      }

      // Test network connectivity
      try {
        await web3Instance.eth.getBlockNumber();
      } catch (networkError) {
        return { connected: false, accounts: [], error: `Network error: ${networkError.message}` };
      }

      // Get accounts
      const accounts = await web3Instance.eth.getAccounts();

      if (accounts.length === 0) {
        // Check if MetaMask is available
        if (typeof window !== 'undefined' && window.ethereum) {
          // MetaMask is available but not connected - this is a normal state
          return {
            connected: false,
            accounts: [],
            error: 'Please connect your wallet first',
          };
        } else {
          // MetaMask not available
          return {
            connected: false,
            accounts: [],
            error: 'MetaMask not detected. Please install MetaMask.',
          };
        }
      }

      return { connected: true, accounts };
    } catch (error) {
      return { connected: false, accounts: [], error: `Validation error: ${error.message}` };
    }
  },

  async callContractMethod<T>(
    contractMethod: { call: () => Promise<T> },
    options: { timeout?: number; retries?: number; retryDelay?: number } = {}
  ): Promise<T> {
    const { timeout = 10000, retries = 3, retryDelay = 1000 } = options;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        // Create timeout promise
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Contract call timeout')), timeout)
        );

        // Race between contract call and timeout
        const result = await Promise.race([contractMethod.call(), timeoutPromise]);

        return result as T;
      } catch (error) {
        lastError = error as Error;

        // Check if this is a recoverable error
        const isRecoverableError =
          error.message?.includes('Internal JSON-RPC error') ||
          error.message?.includes('timeout') ||
          error.message?.includes('network') ||
          error.message?.includes('connection') ||
          error.message?.includes('fetch') ||
          error.message?.includes('Network Error');

        if (!isRecoverableError || attempt === retries - 1) {
          // Non-recoverable error or last attempt
          break;
        }

        // Wait before retrying with exponential backoff
        const delay = retryDelay * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw new Error(
      `Contract method call failed after ${retries} attempts: ${lastError?.message || 'Unknown error'}`
    );
  },
};
